import { relevesBanqueApi } from '../api/resources';
import { jourIso } from './rapprochement-sorties';

/**
 * Ce qu'on garde d'un relevé importé, à côté de son fichier : la banque
 * quand le fichier la nomme, la période couverte, le nombre de lignes et les
 * totaux. Le serveur garde le fichier tel quel ; ces valeurs servent à le
 * montrer dans le classeur sans le relire.
 */

/** Le début d'un BIC suisse (« UBSWCHZH80A ») → la banque, en court. */
const BIC = {
  UBSW: 'UBS',
  POFI: 'PostFinance',
  RAIF: 'Raiffeisen',
  CRES: 'Credit Suisse',
  ZKBK: 'ZKB',
  BCGE: 'BCGE',
  BCVL: 'BCV',
  MIGR: 'Banque Migros',
  VABE: 'Valiant',
  BEKB: 'BEKB',
  LUKB: 'LUKB',
  KBSG: 'SGKB',
  BKBB: 'BKB',
  AKBK: 'AKB',
  BLKB: 'BLKB',
  BCNN: 'BCN',
  BCVS: 'BCVs',
  BEFR: 'BCF',
  BCJU: 'BCJ',
  GRKB: 'GKB',
  KBTG: 'TKB',
};

/** Les noms qu'un relevé en PDF imprime, du plus précis au plus court. */
const NOMS = [
  [/Banque Cantonale Vaudoise|\bBCV\b/, 'BCV'],
  [/Banque Cantonale de Gen[eè]ve|\bBCGE\b/i, 'BCGE'],
  [/Banque Cantonale Neuch[aâ]teloise|\bBCN\b/, 'BCN'],
  [/Banque Cantonale du Valais|Walliser Kantonalbank|\bBCVs\b/, 'BCVs'],
  [/Banque Cantonale de Fribourg|Freiburger Kantonalbank/i, 'BCF'],
  [/Banque Cantonale du Jura/i, 'BCJ'],
  [/Z[üu]rcher Kantonalbank|\bZKB\b/, 'ZKB'],
  [/Berner Kantonalbank|\bBEKB\b/, 'BEKB'],
  [/Luzerner Kantonalbank|\bLUKB\b/, 'LUKB'],
  [/St\.?\s?Galler Kantonalbank|\bSGKB\b/, 'SGKB'],
  [/Basellandschaftliche Kantonalbank|\bBLKB\b/, 'BLKB'],
  [/Basler Kantonalbank/i, 'BKB'],
  [/Aargauische Kantonalbank/i, 'AKB'],
  [/Graub[üu]ndner Kantonalbank/i, 'GKB'],
  [/Thurgauer Kantonalbank/i, 'TKB'],
  [/PostFinance/i, 'PostFinance'],
  [/Raiffeisen/i, 'Raiffeisen'],
  [/Credit Suisse/i, 'Credit Suisse'],
  [/Banque Migros|Migros\s?Bank/i, 'Banque Migros'],
  [/Valiant/i, 'Valiant'],
  [/Bank Cler/i, 'Bank Cler'],
  [/Alternative Bank/i, 'Alternative Bank'],
  [/Revolut/i, 'Revolut'],
  [/\bUBS\b/, 'UBS'],
];

/** Un BIC suisse lisible dans le texte : « BIC UBSWCHZH80A ». */
const BIC_DANS_LE_TEXTE = /\b([A-Z]{4})CH[A-Z0-9]{2}(?:[A-Z0-9]{3})?\b/g;

/** L'établissement d'un IBAN suisse (5 chiffres après « CHkk »). */
function banqueDeLIban(iban) {
  const m = String(iban || '')
    .replace(/\s/g, '')
    .toUpperCase()
    .match(/^CH\d{2}(\d{5})/);
  if (!m) return '';
  const iid = m[1];
  if (iid === '09000') return 'PostFinance';
  if (/^002\d\d$/.test(iid)) return 'UBS';
  if (/^8\d{4}$/.test(iid)) return 'Raiffeisen';
  if (iid === '00700') return 'ZKB';
  if (iid === '00767') return 'BCV';
  if (iid === '00788') return 'BCGE';
  if (iid === '04835') return 'Credit Suisse';
  return '';
}

/**
 * La banque d'un relevé camt.053 : celle qui tient le compte (son BIC, puis
 * son nom), à défaut l'établissement de l'IBAN.
 *
 * @param {string} xml le relevé
 * @returns {string} la banque, ou '' si le fichier ne la nomme pas
 */
export function banqueDuCamt(xml) {
  try {
    const doc = new DOMParser().parseFromString(String(xml || ''), 'text/xml');
    if (doc.getElementsByTagName('parsererror').length) return '';
    const acct = doc.getElementsByTagName('Acct')[0];
    if (!acct) return '';
    const svcr = acct.getElementsByTagName('Svcr')[0];
    const texte = (el, tag) => (el?.getElementsByTagName(tag)[0]?.textContent || '').trim();
    const bic = (texte(svcr, 'BICFI') || texte(svcr, 'BIC')).toUpperCase();
    if (bic && BIC[bic.slice(0, 4)]) return BIC[bic.slice(0, 4)];
    const nom = texte(svcr, 'Nm');
    if (nom) return banqueDuTexte(nom) || nom.slice(0, 80);
    return banqueDeLIban(texte(acct, 'IBAN'));
  } catch {
    return '';
  }
}

/**
 * La banque nommée dans le texte d'un relevé en PDF : le nom ou le BIC qui
 * vient le plus tôt (l'en-tête), pour ne pas prendre la banque d'un
 * paiement plus bas dans la liste.
 *
 * @param {string} texte le texte lu (la première page suffit)
 * @returns {string} la banque, ou '' si aucune n'est lisible
 */
export function banqueDuTexte(texte) {
  const s = String(texte || '');
  let mieux = null;
  for (const [motif, nom] of NOMS) {
    const m = s.match(motif);
    if (m && (mieux === null || m.index < mieux.index)) mieux = { index: m.index, nom };
  }
  for (const m of s.matchAll(BIC_DANS_LE_TEXTE)) {
    const nom = BIC[m[1]];
    if (nom && (mieux === null || m.index < mieux.index)) mieux = { index: m.index, nom };
  }
  return mieux ? mieux.nom : '';
}

const centimes = (n) => Math.round((+n || 0) * 100) / 100;

/**
 * Ce que le classeur montre d'un relevé : du premier au dernier mouvement,
 * le nombre de lignes, le total des entrées et des sorties.
 *
 * @param {object[]} mouvements mouvements lus ({date, montant, sens})
 * @returns {{debut: string, fin: string, lignes: number, entrees: number, sorties: number}}
 *   jours « AAAA-MM-JJ » (vides si aucun) et totaux en francs
 */
export function resumeDuReleve(mouvements) {
  const tx = Array.isArray(mouvements) ? mouvements : [];
  const jours = tx
    .map((t) => jourIso(t?.date))
    .filter(Boolean)
    .sort();
  const somme = (sens) =>
    centimes(tx.filter((t) => t?.sens === sens).reduce((s, t) => s + (+t.montant || 0), 0));
  return {
    debut: jours[0] || '',
    fin: jours[jours.length - 1] || '',
    lignes: tx.length,
    entrees: somme('CRDT'),
    sorties: somme('DBIT'),
  };
}

/**
 * Fait garder par le serveur le fichier d'un relevé importé, tel que
 * transmis, avec ce qui en a été lu. Le même fichier réimporté rend le relevé
 * déjà gardé : il ne fait jamais deux relevés.
 *
 * @param {Blob} fichier le relevé choisi (PDF ou camt.053)
 * @param {object[]} mouvements les mouvements lus dans ce fichier
 * @param {string} [banque] la banque lue, ou ''
 * @returns {Promise<{releve: object, deja: boolean}>} le relevé gardé
 */
export function gardeLeReleve(fichier, mouvements, banque) {
  return relevesBanqueApi.garde(fichier, { banque, ...resumeDuReleve(mouvements) });
}
